import { useState } from 'react';
import Tippy from '@tippyjs/react';
import emailjs from '@emailjs/browser';
import { emailjsConfig } from '../lib/emailjs-config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheckCircle, faExclamationCircle, faSpinner, faPaperPlane, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';

export default function ContactForm({ enabled, isOpen, onClose }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Préparation des données pour EmailJS
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        subject: formData.subject,
        message: formData.message,
        to_name: 'Didi Orlog SOSSOU',
        to_email: 'didisossou@gmail.com',
        reply_to: formData.email
      };

      // Envoi avec EmailJS
      await emailjs.send(
        emailjsConfig.serviceId, 
        emailjsConfig.templateId, 
        templateParams, 
        emailjsConfig.publicKey
      );
      
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      // Fermer le formulaire après 3 secondes
      setTimeout(() => {
        onClose();
        setSubmitStatus(null);
      }, 3000);
      
    } catch (error) {
      console.error('Erreur EmailJS:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-0 sm:p-4 bg-gradient-to-br from-black/60 via-gray-900/40 to-black/60 backdrop-blur-md overflow-y-auto">
      <div className={`relative w-full max-w-xs sm:max-w-lg md:max-w-2xl mx-auto rounded-3xl shadow-2xl transition-all duration-300 border-0 ${
        enabled ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900' : 'bg-gradient-to-br from-white via-gray-100 to-white'
      } sm:max-h-[92vh] sm:overflow-visible max-h-[70vh] overflow-y-auto custom-scrollbar`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${
          enabled ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <h2 className={`text-xl font-semibold ${enabled ? 'text-white' : 'text-gray-800'}`}>
            Contact
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${
              enabled 
                ? 'text-gray-400 hover:text-white hover:bg-gray-800' 
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
            }`}
          >
            <FontAwesomeIcon icon={faTimes} className="text-lg" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-100 border border-green-300 rounded-lg">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-3 text-xl" />
                <div>
                  <p className="text-green-800 font-semibold">Message envoyé avec succès !</p>
                  <p className="text-green-700 text-sm">Je vous répondrai dans les plus brefs délais.</p>
                </div>
              </div>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-lg">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faExclamationCircle} className="text-red-500 mr-3 text-xl" />
                <div>
                  <p className="text-red-800 font-semibold">Erreur lors de l'envoi</p>
                  <p className="text-red-700 text-sm">Veuillez réessayer ou me contacter directement.</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* Nom */}
              <div className="mb-2 sm:mb-0">
                <label className={`block text-sm font-medium mb-2 ${
                  enabled ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Nom complet *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    enabled 
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  placeholder="Votre nom complet"
                />
              </div>

              {/* Email */}
              <div className="mb-2 sm:mb-0">
                <label className={`block text-sm font-medium mb-2 ${
                  enabled ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                    enabled 
                      ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                  }`}
                  placeholder="votre@email.com"
                />
              </div>
            </div>

            {/* Sujet */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                enabled ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Sujet *
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent ${
                  enabled 
                    ? 'bg-gray-800 border-gray-600 text-white' 
                    : 'bg-white border-gray-300 text-gray-900'
                }`}
              >
                <option value="">Sélectionnez un sujet</option>
                <option value="collaboration"> Collaboration / Projet</option>
                <option value="freelance"> Mission Freelance</option>
                <option value="emploi"> Opportunité d'emploi</option>
                <option value="conseil"> Conseil / Consultation</option>
                <option value="formation"> Formation / Cours</option>
                <option value="autre"> Autre</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${
                enabled ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Message *
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className={`w-full px-4 py-3 rounded-lg border transition-all duration-200 focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-y ${
                  enabled 
                    ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="Décrivez votre projet, vos besoins ou votre message..."
              />
            </div>

            {/* Boutons */}
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 pt-2 sm:pt-4 w-full">
              <button
                type="button"
                onClick={onClose}
                className={`w-full sm:flex-1 px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-200 hover:scale-105 active:scale-95 ${
                  enabled 
                    ? 'bg-gray-700 hover:bg-gray-600 text-white border border-gray-600' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300'
                }`}
              >
                <FontAwesomeIcon icon={faTimes} className="mr-2" />
                Annuler
              </button>

              <Tippy 
                content={isSubmitting ? "Envoi en cours..." : "Envoyer le message"} 
                animation="shift-away" 
                arrow={false}
              >
                <button
                  type="submit"
                  disabled={isSubmitting || !formData.name || !formData.email || !formData.subject || !formData.message}
                  className="w-full sm:flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed text-white font-semibold px-4 sm:px-6 py-3 rounded-lg transition-all duration-200 hover:scale-105 active:scale-95 hover:shadow-lg disabled:hover:scale-100"
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon icon={faSpinner} className="fa-spin mr-2" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faPaperPlane} className="mr-2" />
                      Envoyer le message
                    </>
                  )}
                </button>
              </Tippy>
            </div>
          </form>

          {/* Contact direct */}
          <div className={`mt-6 pt-6 border-t text-center ${
            enabled ? 'border-gray-800' : 'border-gray-300'
          }`}>
            <p className={`text-sm mb-3 ${enabled ? 'text-gray-400' : 'text-gray-600'}`}>
              Ou contactez-moi directement :
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="mailto:didisossou@gmail.com"
                className={`inline-flex items-center px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 ${
                  enabled 
                    ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900'
                }`}
              >
                <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-orange-500" />
                didisossou@gmail.com
              </a>
              <a
                href="https://linkedin.com/in/didi-orlog-sossou"
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center px-4 py-2 rounded-lg transition-all duration-200 hover:scale-105 ${
                  enabled 
                    ? 'bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white' 
                    : 'bg-gray-100 hover:bg-gray-200 text-gray-700 hover:text-gray-900'
                }`}
              >
                <FontAwesomeIcon icon={faLinkedin} className="mr-2 text-blue-500" />
                LinkedIn
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
